import React, { useState } from 'react'
import { Linking, StyleSheet, View } from 'react-native'
import { Button, Text } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { whatsappUrl } from '../utils/phoneNumber'

/**
 * ATL-1523 — every "write on WhatsApp" button in the app, in one place.
 *
 * The four screens that had one each cleaned the phone their own way (one did not clean it at all)
 * and called `Linking.openURL` with no error handling, so a phone carrying an invisible character, a
 * phone with no digits, or a device that could not open the link all produced the same result: a tap
 * that did nothing. Here the link is built from the phone's digits only (`whatsappUrl`), and a phone
 * with no digits or a refused `openURL` is SAID, under the button, instead of swallowed.
 *
 * Props: `phone` (any shape — only its digits are used), optional `text` (the pre-filled message, or
 * a function returning it, evaluated on tap — the receipt message is decoded from base64 and a decode
 * failure must be reported like any other), `testID`, and the Button's own `status` / `appearance` /
 * `style`. `allowChatPicker`: with no phone, open WhatsApp's chat picker with the message instead of
 * refusing (the receipt modal). Children are the button label.
 */
export function WhatsAppButton({ phone, text, allowChatPicker = false, testID, status = 'success', appearance, style, children }) {
  const { t } = useTranslation()
  const [error, setError] = useState(null)

  const open = async () => {
    try {
      const url = whatsappUrl(phone, typeof text === 'function' ? text() : text, { allowChatPicker })
      if (!url) {
        setError(t('whatsapp.noPhone'))
        return
      }
      setError(null)
      await Linking.openURL(url)
    } catch (err) {
      setError(t('whatsapp.openFailed'))
      Sentry.captureException(err, { tags: { function: 'WhatsAppButton' } })
    }
  }

  return (
    <View>
      <Button testID={testID} status={status} appearance={appearance} style={style} onPress={open}>
        {children}
      </Button>
      {error && (
        <Text testID={testID ? `${testID}-error` : undefined} status='danger' category='c1' style={styles.error}>
          {error}
        </Text>
      )}
    </View>
  )
}

const styles = StyleSheet.create({ error: { marginTop: 4 } })

export default WhatsAppButton
